/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ErrorMessage from '../common/ErrorMessage.jsx';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import {
    selectOfficeObj,
    setOfficeObj,
    updateOfficeActiveState
} from '../../redux/slices/office-slice.js';
import TableTitleHeader from '../common/TableTitleHeader.jsx';
import { yellow, blue } from '../../styles/standard-colors.js';
import CopyOfficeInfo from './CopyOfficeInfo.jsx';
import OfficeInfoTable from './OfficeInfoTable.jsx';
import ConfirmationModal from '../common/ConfirmationModal.jsx';
import useOffices from '../../hooks/useOffices.js';
import CreateUpdateOfficeForm from './CreateUpdateOfficeForm.jsx';
import SettingsDropdown from './SettingsDropdown.jsx';

/**
 * Component for displaying office information.
 * @returns {JSX.Element} JSX element representing the Office Information Container.
 */
const OfficeInfoContainer = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [showDropdown, setShowDropdown] = useState(false);
    const [showCopy, setShowCopy] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [showDuplicateOfficeNoteModal, setShowDuplicateOfficeNoteModal] =
        useState(false);
    const [showRetireOfficeModal, setShowRetireOfficeModal] = useState(false);

    const officeObj = useSelector(selectOfficeObj);
    const { error, loading, duplicateOffice, updateOffice } = useOffices();

    /**
     * SINGLE SOURCE OF TRUTH FOR RETIRE / ACTIVATE
     */
    const handleChangeActiveState = async () => {
        const updateFields = { active: !officeObj.active };

        const updatedOffice = await updateOffice(
            officeObj.id,
            updateFields,
            officeObj
        );

        if (!updatedOffice) return;

        dispatch(setOfficeObj(updatedOffice));
        dispatch(updateOfficeActiveState(updatedOffice.active));
    };

    /**
     * Handles duplication of the office.
     * @returns {void}
     */
    const handleDuplication = async () => {
        setShowDuplicateOfficeNoteModal(false);

        const duplicatedOffice = await duplicateOffice(officeObj.id);

        // Check for failure
        if (!duplicatedOffice) {
            return;
        }

        navigate(`/office/${duplicatedOffice.id}`);
    };

    /**
     * Formats a timestamp to Oracle-style: DD-MMM-YYYY HH:MM AM/PM
     * Example: 28-AUG-2025 01:15 PM
     */
    const formatOracleTimestamp = (timestamp) => {
        if (!timestamp) return '';

        const date = new Date(timestamp);

        const day = String(date.getDate()).padStart(2, '0');

        const monthNames = [
            'JAN',
            'FEB',
            'MAR',
            'APR',
            'MAY',
            'JUN',
            'JUL',
            'AUG',
            'SEP',
            'OCT',
            'NOV',
            'DEC'
        ];
        const month = monthNames[date.getMonth()];

        const year = date.getFullYear();

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12; // Convert 0 => 12 for 12 AM
        const formattedTime = `${hours}:${minutes} ${ampm}`;

        return `${day}-${month}-${year} ${formattedTime}`;
    };

    return (
        <div className="office-container">
            <TableTitleHeader
                includeBackArrow={true}
                title={officeObj.officeSysName}
                // subtitle={
                //     officeObj.retiredAt
                //         ? `Retired by: ${
                //               officeObj.retiredBy || 'Unknown'
                //           } at ${new Date(
                //               officeObj.retiredAt
                //           ).toLocaleString()}`
                //         : null
                // }
                subtitle={
                    officeObj.retiredAt
                        ? `Retired by: ${
                              officeObj.retiredBy || 'Unknown'
                          } at ${formatOracleTimestamp(officeObj.retiredAt)}`
                        : null
                }
                hasSettingsGear={true}
                settingsGearColor={yellow}
                setShowDropdown={setShowDropdown}
                showDropdown={showDropdown}
                customContainerStyles={{ marginLeft: '30px' }}
            />
            {officeObj.autoGeneratedFromHub && (
                <div
                    style={{
                        marginLeft: '30px',
                        marginBottom: '1em',
                        display: 'inline-block',
                        fontSize: '0.8em',
                        fontWeight: 700,
                        color: '#fff',
                        backgroundColor: blue,
                        borderRadius: '4px',
                        padding: '3px 8px'
                    }}
                    title="This note was auto-created from Hub, verify all details before install">
                    Auto-generated from Hub
                    {officeObj.hubSyncedAt &&
                        ` • ${formatOracleTimestamp(officeObj.hubSyncedAt)}`}
                </div>
            )}
            {loading && <LoadingSpinner />}
            {error && <ErrorMessage message={error.message} />}
            {showDropdown && (
                <SettingsDropdown
                    showCopy={showCopy}
                    setShowCopy={setShowCopy}
                    showEdit={showEdit}
                    setShowEdit={setShowEdit}
                    showDuplicateOfficeNoteModal={showDuplicateOfficeNoteModal}
                    setShowDuplicateOfficeNoteModal={
                        setShowDuplicateOfficeNoteModal
                    }
                    setShowRetireOfficeModal={setShowRetireOfficeModal}
                />
            )}
            {officeObj.inSecureProtocol && (
                <div style={{ marginLeft: '30px' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '2em' }}>
                        **DO NOT CONNECT OFFICE IS IN VIRUS PROTOCOL**
                    </h3>
                </div>
            )}
            {showCopy ? (
                <CopyOfficeInfo />
            ) : showEdit ? (
                <CreateUpdateOfficeForm
                    objectToUpdate={officeObj}
                    setShowEdit={setShowEdit}
                />
            ) : (
                <OfficeInfoTable />
            )}
            {showDuplicateOfficeNoteModal && (
                <ConfirmationModal
                    title="Duplicate Office Note"
                    message="Are you sure you want to duplicate this office note?"
                    onConfirm={handleDuplication}
                    setShowModal={setShowDuplicateOfficeNoteModal}
                />
            )}
            {/* RETIRE CONFIRMATION */}
            {showRetireOfficeModal && (
                <ConfirmationModal
                    title="Retire Office"
                    message="Are you sure you want to retire this office? This action cannot be easily undone."
                    onConfirm={async () => {
                        setShowRetireOfficeModal(false);
                        await handleChangeActiveState();
                        setShowDropdown(false);
                    }}
                    setShowModal={setShowRetireOfficeModal}
                />
            )}
        </div>
    );
};

export default OfficeInfoContainer;
